import React, { useState } from "react";
import { Button } from "../../components/ui";
import { MapIcon } from "@heroicons/react/solid";
import useDriverTrips from "../../hooks/trips/useDriverTrips";
import Pagination from "../../components/pagination";
import Spinner from "../../components/loader/Spinner";
import NoDataFound from "../../components/ui/NoDataFound";
import { useSearchParams } from "react-router-dom";
import DateRangePicker from "../../components/ui/DateRangePicker";
import InlineLoader from "../../components/loader/InlineLoader";
import TripLogsModal from "./TripLogsModal";
import BreadCrumbs from "../../components/ui/BreadCrumbs";
import RoundedContainer from "../../components/containers/RoundedContainer";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeaderCell,
	TableRow,
} from "../../components/table/Table";

const Trips = () => {
	const [searchParams] = useSearchParams();
	const company_employee_id = searchParams.get("driver_id");
	const breadcrumbs = [
		{
			label: "Trips",
			link: "/live_tracking/users",
		},
		{
			label: "Trip Details",
			link: "",
		},
	];
	const [selectedTrip, setSelectedTrip] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const {
		driverTripsData,
		isLoading,
		pageNo,
		fromDate,
		toDate,
		handlePageClick,
		handleDateRangeChange,
		calculateTripDuration,
	} = useDriverTrips(company_employee_id);

	const driverDetails = driverTripsData?.data?.driver_details || {};
	const driverTrips = driverTripsData?.data?.data || [];
	const totalCount = driverTripsData?.data?.total_count || 0;
	const perPage = driverTripsData?.data?.per_page || 10;

	const handleOpenModal = (trip) => {
		setSelectedTrip(trip);
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	return (
		<div className="px-5 py-4">
			<BreadCrumbs crumbs={breadcrumbs} />
			<div className="mb-4 flex items-center justify-between">
				<h1 className="text-xl font-semibold flex items-center gap-2">
					{isLoading ? (
						<>
							<InlineLoader className="text-color5 h-5 w-5" /> Trips
						</>
					) : driverDetails?.name ? (
						`${driverDetails.name}'s Trips`
					) : driverDetails?.first_name ? (
						`${driverDetails.first_name}${
							driverDetails?.last_name ? " " + driverDetails.last_name : ""
						}'s Trips`
					) : (
						"Trips"
					)}
				</h1>
			</div>

			<RoundedContainer className="mb-4">
				<div className="flex justify-end items-center">
					<div className="w-[300px]">
						<DateRangePicker
							label="Filter by Date Range"
							startDate={fromDate}
							endDate={toDate}
							onChange={handleDateRangeChange}
							className="w-full"
						/>
					</div>
				</div>
			</RoundedContainer>

			{isLoading ? (
				<div className="flex justify-center items-center py-20">
					<Spinner />
				</div>
			) : (
				<>
					<RoundedContainer>
						{driverTrips.length <= 0 ? (
							<div className="py-8">
								<NoDataFound message={"No Trips Found"} />
							</div>
						) : (
							<div className="max-w-full overflow-x-auto">
								<Table className="table-auto w-full">
									<TableHead>
										<TableHeaderCell className="w-[10%] text-center">
											Sr.No
										</TableHeaderCell>
										<TableHeaderCell className="w-[28%] text-center">
											Trip Start
										</TableHeaderCell>
										<TableHeaderCell className="w-[28%] text-center">
											Trip End
										</TableHeaderCell>
										<TableHeaderCell className="w-[20%] text-center">
											Total Duration
										</TableHeaderCell>
										<TableHeaderCell className="w-[14%] text-center">
											View
										</TableHeaderCell>
									</TableHead>
									<TableBody>
										{driverTrips.map((trip, index) => (
											<TableRow key={trip.trip_id || index}>
												<TableCell className="text-center text-dark font-medium text-sm">
													{pageNo * perPage + index + 1}
												</TableCell>
												<TableCell className="text-center text-dark font-medium text-sm">
													{trip.start_date
														? new Date(trip.start_date * 1000).toLocaleString()
														: "-"}
												</TableCell>
												<TableCell className="text-center text-dark font-medium text-sm">
													{trip.end_date
														? new Date(trip.end_date * 1000).toLocaleString()
														: "-"}
												</TableCell>
												<TableCell className="text-center text-dark font-medium text-sm">
													{calculateTripDuration(
														trip.start_time,
														trip.end_time
													)}
												</TableCell>
												<TableCell className="text-center text-dark font-medium text-sm">
													<div className="flex items-center justify-center w-full">
														<Button
															className="space-x-1.5"
															variant="outline"
															size="sm"
															onClick={() => handleOpenModal(trip)}
														>
															<MapIcon className="w-4 h-4" />
															<span>View</span>
														</Button>
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
					</RoundedContainer>

					{/* Trip Logs Modal */}
					{selectedTrip && isModalOpen && (
						<TripLogsModal
							open={isModalOpen}
							onClose={handleCloseModal}
							trip={selectedTrip}
							company_employee_id={company_employee_id}
							driverDetails={driverDetails}
						/>
					)}

					{totalCount > perPage && (
						<RoundedContainer className="mt-4">
							<Pagination
								currentPage={pageNo}
								lengthofItems={totalCount}
								limit={perPage}
								onPageChange={handlePageClick}
							/>
						</RoundedContainer>
					)}
				</>
			)}
		</div>
	);
};

export default Trips;
